/* global dataLayer */

/* eslint-disable import/first */
import { useEffect } from 'react';
import NoSSR from 'react-no-ssr';
import Metrics from '@spotahome/soyuz-tracking';

import {
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  GoogleOneTap,
  UserInfoProvider,
  generateFontPreload,
  TenantTypeProvider,
  AppBanner,
  CitiesProvider,
  Footer,
  DeviceProvider
} from '@spotahome/ui-library';
import { useCoreWebVitals } from '@spotahome/ui-library/src/hooks/useCoreWebVitals';
import usePageLoadTime from '@spotahome/ui-library/src/hooks/usePageLoadTime';
import LandingTestimonialsSection from '@spotahome/ui-library/src/tenant/components/LandingTestimonialsSection';

import {
  useSoyuzData,
  useSoyuzBrandConfig,
  useSoyuzFlag,
  useSoyuzExperiment,
  trans
} from '@spotahome/soyuz/client';

import getGqlClient from '@spotahome/landings-common/src/graphql/client';

import { sendTenantActionEvent } from '@spotahome/landings-common/src/graphql/mutations/tenantActions';

import LazyLoginSignUpModal from '@spotahome/landings-common/src/components/LoginSignupModal';
import OurGuarantee from '@spotahome/ui-library/src/tenant/components/OurGuarantee';
import FAQSection from '@spotahome/ui-library/src/tenant/components/FAQSection';

import PropertyTypeSection from '@spotahome/landings-common/src/components/PropertyTypeSection';
import CitiesShowcase from '@spotahome/landings-common/src/components/CitiesShowcase';
import TestimonialsCarousel from '@spotahome/landings-common/src/components/TestimonialsCarousel/TestimonialsCarousel';
import OptInWrapper from '@spotahome/landings-common/src/components/OptInWrapper';

import {
  getTenantFaq,
  getTenantStructuredFaq
} from '@spotahome/landings-common/src/utils/faq';

import FindYourHome from '@spotahome/ui-library/src/tenant/components/FindYourHome';

import {
  CustomDimensionsManager,
  constants
} from '@spotahome/ecommerce-tracking';

import '@spotahome/landings-common/src/scss/init.scss';

import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';

import Hero from './components/Hero';
import GuaranteeSection from './components/GuaranteeSection';
import LandlordSection from './components/LandlordSection';

import './main.scss';

const getTenantStructuredFaqScript = trans => ({
  type: 'application/ld+json',
  innerHTML: JSON.stringify(getTenantStructuredFaq(trans))
});

const getPreloadImageLink = imageUrl => {
  if (!imageUrl) return null;

  return {
    rel: 'preload',
    as: 'image',
    href: imageUrl
  };
};

const EXPERIMENT_IMAGES = [
  'https://sah-public-assets.s3.eu-west-1.amazonaws.com/images/home-monuments-redesign-mobile-white.svg'
];

const IMAGES_TO_PRELOAD = {
  smartphone: EXPERIMENT_IMAGES,
  tablet: EXPERIMENT_IMAGES,
  desktop: EXPERIMENT_IMAGES
};

const LandingPage = () => {
  const { [LandingPage.NAME]: requestedData } = useSoyuzData();
  const { isPartner } = useSoyuzBrandConfig();
  const showGracePeriodCancellationPolicies = useSoyuzFlag(
    'GracePeriodCancellationPolicies'
  ).isOn();
  const isTrustpilotTestimonialHomepageVariant =
    useSoyuzExperiment('TrustpilotTestimonialHomepage').get('behaviour') ===
    'variant';

  const { userId, initialCookie, isSafari, deviceType, deviceViewType } =
    requestedData;

  useCoreWebVitals();
  usePageLoadTime();

  useEffect(() => {
    Metrics.actions.configureClient(async ({ actionId, payload }) => {
      try {
        await sendTenantActionEvent(getGqlClient())({
          actionId,
          payload
        });
      } catch (e) {
        console.log('error sending sendTenantActionEvent:', e);
      }
    });
  }, []);

  useEffect(() => {
    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({ userId });
    }
  }, [userId]);

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider>
          <DeviceProvider deviceType={deviceType}>
            <CitiesProvider>
              <ZohoChatbot />
              <AppBanner source="home" deviceType={deviceType} />
              <ToastContainer />
              {!userId && <GoogleOneTap />}
              <TenantTypeProvider>
                <div data-browser={isSafari ? 'safari' : 'other'}>
                  <Hero deviceType={deviceType} />
                  {!isPartner && (
                    <>
                      <GuaranteeSection />
                      {isTrustpilotTestimonialHomepageVariant ? (
                        <LandingTestimonialsSection
                          renderTitle={() => (
                            <h3 className="landing__testimonials__title">
                              {trans('how_it_works.testimonials.title')}
                            </h3>
                          )}
                        />
                      ) : (
                        <TestimonialsCarousel />
                      )}
                      <OurGuarantee />
                      <PropertyTypeSection deviceType={deviceType} />
                      <LandlordSection />
                      <CitiesShowcase deviceType={deviceType} />
                      <FAQSection
                        questions={getTenantFaq(
                          showGracePeriodCancellationPolicies
                        )}
                        theme="homepage"
                      />
                      <FindYourHome />
                      <Footer theme="beige" />
                      <OptInWrapper />
                    </>
                  )}
                  <NoSSR>
                    <LazyLoginSignUpModal deviceViewType={deviceViewType} />
                  </NoSSR>
                </div>
              </TenantTypeProvider>
            </CitiesProvider>
          </DeviceProvider>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

LandingPage.requestAllData = async req => {
  const imagesToPreload = (
    IMAGES_TO_PRELOAD[req.device.type] || IMAGES_TO_PRELOAD.desktop
  ).map(i => getPreloadImageLink(i));
  const fontPreload = generateFontPreload(req.brandConfig);
  try {
    const { locale, userId } = req;
    const cdManager = CustomDimensionsManager();
    cdManager.setCommonCustomDimensions({
      userId,
      locale,
      pageType: constants.TRACKING_PAGE_TYPES.HOMEPAGE
    });

    const isSafari =
      req.device.info && req.device.info.client
        ? req.device.info.client.name.toLowerCase().includes('safari')
        : false;

    return {
      headers: {
        scripts: [
          { innerHTML: cdManager.flush() },
          getTenantStructuredFaqScript(
            req.trans,
            req.getFlag('GracePeriodCancellationPolicies').isOn()
          )
        ],
        links: [...fontPreload, ...imagesToPreload]
      },
      data: {
        userId: req.userId,
        deviceType: req.device.type,
        deviceViewType: req.device.viewType,
        initialCookie: req.headers.cookie,
        isSafari
      }
    };
  } catch (err) {
    req.logger.error('Landing page error', { err });
  }

  return {
    headers: {
      links: [...fontPreload, ...imagesToPreload]
    },
    data: {
      userId: null,
      topHomecards: null
    }
  };
};

LandingPage.NAME = 'LandingPage';

export default LandingPage;
